import { type Home, type User, UserRole } from '@lmc/ts-common'
import { Icon, IconType } from '@lmc/ui-kit'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { ConfigProvider, Menu } from 'antd'
import useCarer from 'hooks/useCarer'
import useHomeType from 'hooks/useHomeType'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/team'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import {
  type CustomMenuItemType,
  getNavigationMenuItems,
  isGroupUser,
  navigationTheme
} from 'utils'
import { useTeamMemberSidebarNavigation } from '../hooks/useTeamMemberSidebarNavigation'
import TeamMemberSideNavigationCard from './TeamMemberSideNavigationCard'

type TeamSidebarNavigationFlagProps = {
  skillsAndNeedsGranted?: boolean
  proEnabled?: boolean
  hasRota?: boolean
  isIt: boolean
  isNotGroupRole: boolean
}

export const getMenuItems = ({
  skillsAndNeedsGranted,
  proEnabled,
  hasRota,
  isIt,
  isNotGroupRole
}: TeamSidebarNavigationFlagProps) => {
  return [
    {
      label: 'Profile',
      url: 'profile',
      icon: <Icon customIcon={IconType.SideProfile} />
    },
    ...(skillsAndNeedsGranted
      ? [
          {
            label: 'Training and Skills',
            url: 'skills',
            icon: <Icon customIcon={IconType.TrainingAndSkillsIcon} />
          }
        ]
      : []),
    ...(proEnabled
      ? [
          {
            label: hasRota ? 'Contract and Preferences' : 'Contract',
            url: 'contract-and-preferences',
            icon: <Icon customIcon={IconType.ContractAndPreferencesIcon} />,
            id: 'contract-and-preferences-menu-item',
            'data-intercom-target': 'contract-and-preferences-menu-item'
          }
        ]
      : []),
    // CM-3757: absences (rostering) is outside the IT & Security remit, so
    // hide it from the team-member side nav for IT (route is guarded too).
    ...(hasRota && isNotGroupRole && !isIt
      ? [
          {
            label: 'Absences',
            url: 'absences',
            icon: <Icon customIcon={IconType.AbsencesIcon} />,
            id: 'absences-menu-item',
            'data-intercom-target': 'absences-menu-item'
          }
        ]
      : [])
  ]
}

const TeamMemberSideNavigation = ({ testId }: { testId?: string }) => {
  const navigate = useNavigate()
  const { t } = useTranslation(NAMESPACE.TEAM)
  const { id } = useParams()

  const user = useSelector(
    (state: { global: { user: User } }) => state.global.user
  )

  const { hasRota } = useHomeType()

  const isGroup = isGroupUser(user.role)
  const isNotGroupRole = ![
    UserRole.groupAdmin,
    UserRole.groupManager
  ]?.includes(user.role)
  const isIt = user.role === UserRole.it

  const { carer } = useCarer(isNotGroupRole ? id : undefined)

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const items = getNavigationMenuItems(
    getMenuItems({
      skillsAndNeedsGranted: home?.features?.skillsAndNeeds?.granted,
      proEnabled: home?.features?.pro?.enabled,
      hasRota,
      isIt,
      isNotGroupRole
    }) as unknown as CustomMenuItemType[]
  ) as CustomMenuItemType[]

  const { defaultSelectedKey } = useTeamMemberSidebarNavigation()

  const onBack = () => navigate('/organisation/team')

  return (
    <SidebarContainer data-testid={testId}>
      {isGroup && (
        <BackLink onClick={onBack} data-testid="profile-view-back-link">
          <Icon name="ArrowLeftOutlined" />
          {t(reportsConstants.PROFILE_VIEW_BACK_LINK)}
        </BackLink>
      )}
      {carer && (
        <TeamMemberSideNavigationCard
          carer={carer}
          testId={testId ? `${testId}-card` : undefined}
        />
      )}
      <Divider data-testid={testId ? `${testId}-divider` : undefined} />
      <ConfigProvider theme={navigationTheme}>
        <Menu
          data-cy="team-members-menu"
          onClick={({ key }) => navigate(`${id}/${key}`)}
          style={{ border: 'none' }}
          defaultSelectedKeys={[defaultSelectedKey]}
          mode="inline"
          theme="light"
          inlineCollapsed={false}
          items={items}
          data-testid={testId ? `${testId}-menu` : undefined}
        />
      </ConfigProvider>
    </SidebarContainer>
  )
}
const BackLink = styled.button`
  background: none;
  border: none;
  color: #2d82d8;
  cursor: pointer;
  padding: 10px 20px;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;

  &:hover {
    opacity: 0.8;
  }
`

const SidebarContainer = styled.div`
  margin-bottom: 0px;
  background-color: ${(props) => props.theme.color.neutral.color11};
  max-width: 290px;
  width: 100%;
  height: 100%;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-right: 24px;
    margin-left: 24px;
    max-width: unset;
    width: 100%;
  }
  overflow: auto;
`

export default TeamMemberSideNavigation
